import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../lib/api';
import AdminLayout from '../components/AdminLayout';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Users,
  RefreshCw,
  Download,
  Award,
  Dumbbell,
  BarChart2,
  Activity,
  Target,
  AlertCircle,
  UserCheck,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  UserX,
  DollarSign,
  Flame,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';
import { LockedButton } from '../components/UpgradeModal';
import { PlanGate } from '../components/PlanGate';

const COLORS = ['#10b981', '#8b5cf6', '#f59e0b', '#3b82f6', '#ef4444', '#06b6d4', '#ec4899'];

// ── Custom Tooltip ──────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, prefix = '', suffix = '' }) => {
  if (!active || !payload?.length) {
    return null;
  }
  return (
    <div className="bg-white dark:bg-[#12161f] border border-gray-100 dark:border-white/10 shadow-2xl rounded-2xl p-4 min-w-[140px]">
      <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">{label}</p>
      {payload.map((e, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: e.color }} />
          <span className="text-xs text-gray-500 dark:text-gray-400 flex-1">{e.name}</span>
          <span className="font-black text-gray-900 dark:text-white text-sm">
            {prefix}
            {typeof e.value === 'number' ? e.value.toLocaleString() : e.value}
            {suffix}
          </span>
        </div>
      ))}
    </div>
  );
};

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KPICard({ icon: Icon, label, value, sub, color, trend, trendLabel }) {
  const isUp = trend > 0;
  return (
    <div className="bg-white dark:bg-[#0c0f14] border border-gray-200 dark:border-white/[0.07] rounded-2xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300 flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">
            {label}
          </p>
          <p className="text-3xl font-black tracking-tight text-gray-900 dark:text-white">
            {value}
          </p>
          {sub && <p className="text-xs font-medium text-gray-400 mt-1">{sub}</p>}
        </div>
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon size={22} style={{ color }} />
        </div>
      </div>
      {trend !== undefined && trend !== null && (
        <div
          className={`flex items-center gap-1.5 pt-3 border-t border-gray-100 dark:border-white/5 text-xs font-bold ${isUp ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}
        >
          {isUp ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {isUp ? '+' : ''}
          {trend}% {trendLabel || 'vs last month'}
        </div>
      )}
    </div>
  );
}

// ── Section Header ────────────────────────────────────────────────────────────
const SH = ({ title, sub, action }) => (
  <div className="flex items-start justify-between mb-5">
    <div>
      <h2 className="text-lg font-black text-gray-900 dark:text-white tracking-tight">{title}</h2>
      {sub && <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-0.5">{sub}</p>}
    </div>
    {action}
  </div>
);

// ── Tab Button ────────────────────────────────────────────────────────────────
function TabBtn({ active, onClick, children, icon: Icon }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${active ? 'bg-white dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 shadow-sm ring-1 ring-indigo-200 dark:ring-indigo-500/30' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/5'}`}
    >
      {Icon && <Icon size={15} />}
      {children}
    </button>
  );
}

// ── Retention Risk Row ────────────────────────────────────────────────────────
function AtRiskRow({ member, currencySymbol }) {
  const daysInactive = member.days_inactive || 0;
  const riskColor = daysInactive > 20 ? '#ef4444' : daysInactive > 10 ? '#f59e0b' : '#22c55e';
  return (
    <div className="flex items-center gap-4 p-4 rounded-2xl hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors border border-transparent hover:border-gray-100 dark:hover:border-white/5">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-black shadow-sm flex-shrink-0"
        style={{
          background: `hsl(${(member.name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 360},60%,50%)`,
        }}
      >
        {(member.name || 'U').substring(0, 2).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm text-gray-900 dark:text-white truncate">{member.name}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {member.membership_plan} · Last seen {daysInactive}d ago
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        <span
          className="text-xs font-bold px-2 py-1 rounded-full"
          style={{
            color: riskColor,
            backgroundColor: `${riskColor}15`,
            border: `1px solid ${riskColor}30`,
          }}
        >
          {daysInactive > 20 ? '🔴 High Risk' : daysInactive > 10 ? '🟡 At Risk' : '🟢 OK'}
        </span>
      </div>
    </div>
  );
}

export default function ReportsPage() {
  usePageTitle('Reports & Analytics');
  const { currencySymbol } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Revenue Insights state
  const [revenueData, setRevenueData] = useState(null);
  const [revLoading, setRevLoading] = useState(false);

  // Retention state
  const [retentionData, setRetentionData] = useState(null);
  const [retLoading, setRetLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/summary`);
      setData(res.data);
    } catch {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRevenue = useCallback(async () => {
    if (revenueData) {
      return;
    }
    setRevLoading(true);
    try {
      const [summaryRes, monthlyRes] = await Promise.all([
        axios.get(`${API}/revenue/summary`),
        axios.get(`${API}/revenue/monthly`),
      ]);
      setRevenueData({ summary: summaryRes.data, monthly: monthlyRes.data });
    } catch {
      // Fallback: use reports data
      if (data) {
        setRevenueData({ summary: {}, monthly: data.monthly_revenue || [] });
      }
    } finally {
      setRevLoading(false);
    }
  }, [revenueData, data]);

  const fetchRetention = useCallback(async () => {
    if (retentionData) {
      return;
    }
    setRetLoading(true);
    try {
      const [overviewRes, atRiskRes] = await Promise.all([
        axios.get(`${API}/retention/overview`),
        axios.get(`${API}/retention/at-risk`),
      ]);
      setRetentionData({ overview: overviewRes.data, atRisk: atRiskRes.data });
    } catch {
      setRetentionData({ overview: {}, atRisk: [] });
    } finally {
      setRetLoading(false);
    }
  }, [retentionData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'revenue') {
      fetchRevenue();
    }
    if (activeTab === 'retention') {
      fetchRetention();
    }
  }, [activeTab, fetchRevenue, fetchRetention]);

  const downloadCSV = async type => {
    try {
      const res = await axios.get(`${API}/${type}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `fitxero_${type}_export.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${type} CSV downloaded!`);
    } catch {
      toast.error('Export failed');
    }
  };

  const sk = (h = 240) => (
    <div className="animate-pulse bg-gray-100 dark:bg-white/5 rounded-2xl" style={{ height: h }} />
  );

  const TABS = [
    { key: 'overview', label: 'Overview', icon: BarChart2 },
    { key: 'revenue', label: 'Revenue Insights', icon: TrendingUp },
    { key: 'retention', label: 'Retention', icon: UserCheck },
  ];

  return (
    <AdminLayout>
      <div className="page-enter max-w-7xl mx-auto space-y-6 pb-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <BarChart2 size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-gray-900 dark:text-white">
                Reports & Analytics
              </h1>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Business intelligence for your gym
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <LockedButton
              feature="exportReports"
              icon={Download}
              className="px-4 py-2 rounded-xl text-sm font-bold border border-gray-200 dark:border-white/10 bg-white dark:bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 shadow-sm transition-all flex items-center gap-2"
              onClick={() => downloadCSV('members')}
            >
              Members CSV
            </LockedButton>
            <LockedButton
              feature="exportReports"
              icon={Download}
              className="px-4 py-2 rounded-xl text-sm font-bold border border-gray-200 dark:border-white/10 bg-white dark:bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 shadow-sm transition-all flex items-center gap-2"
              onClick={() => downloadCSV('payments')}
            >
              Payments CSV
            </LockedButton>
            <button
              onClick={fetchData}
              className="px-4 py-2 rounded-xl text-sm font-bold border border-gray-200 dark:border-white/10 bg-white dark:bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 shadow-sm transition-all flex items-center gap-2"
            >
              <RefreshCw size={15} /> Refresh
            </button>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="flex gap-1.5 bg-gray-100 dark:bg-[#0c0f14] p-1.5 rounded-2xl w-fit ring-1 ring-gray-200 dark:ring-white/[0.07] shadow-inner overflow-x-auto max-w-full no-scrollbar">
          {TABS.map(t => (
            <TabBtn
              key={t.key}
              active={activeTab === t.key}
              onClick={() => setActiveTab(t.key)}
              icon={t.icon}
            >
              {t.label}
            </TabBtn>
          ))}
        </div>

        {/* ══ OVERVIEW TAB ══ */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* KPI Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {loading ? (
                [1, 2, 3, 4].map(i => <div key={i}>{sk(140)}</div>)
              ) : (
                <>
                  <KPICard
                    icon={TrendingUp}
                    label="This Month Revenue"
                    color="#10b981"
                    value={`${currencySymbol}${(data?.monthly_revenue?.at(-1)?.revenue || 0).toLocaleString()}`}
                    sub="collected payments"
                    trend={data?.mom_change}
                  />
                  <KPICard
                    icon={Users}
                    label="Total Members"
                    color="#3b82f6"
                    value={data?.total_members || 0}
                    sub="current roster"
                  />
                  <KPICard
                    icon={Activity}
                    label="Retention Rate"
                    color="#8b5cf6"
                    value={`${data?.retention_rate || 0}%`}
                    sub="visited in last 30 days"
                  />
                  <KPICard
                    icon={Dumbbell}
                    label="Active Trainers"
                    color="#f59e0b"
                    value={data?.trainer_hours?.length || 0}
                    sub="logged hours this month"
                  />
                </>
              )}
            </div>

            {/* Revenue Trend Chart */}
            <div className="bg-white dark:bg-[#0c0f14] border border-gray-200 dark:border-white/[0.07] rounded-3xl p-7 shadow-sm hover:shadow-md transition-all">
              <SH
                title="Revenue Trend"
                sub="Last 6 months of collected payments"
                action={
                  <button
                    onClick={() => setActiveTab('revenue')}
                    className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                  >
                    Full Insights <ArrowUpRight size={12} />
                  </button>
                }
              />
              {loading ? (
                sk()
              ) : (
                <div className="h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={data?.monthly_revenue}
                      margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.08} />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 11, fill: '#9ca3af', fontWeight: 600 }}
                        axisLine={false}
                        tickLine={false}
                        dy={10}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#9ca3af', fontWeight: 600 }}
                        tickFormatter={v =>
                          `${currencySymbol}${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`
                        }
                        axisLine={false}
                        tickLine={false}
                        dx={-8}
                      />
                      <Tooltip content={<CustomTooltip prefix={currencySymbol} />} />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        name="Revenue"
                        stroke="#10b981"
                        strokeWidth={3}
                        fill="url(#revGrad)"
                        dot={false}
                        activeDot={{ r: 5, fill: '#10b981', stroke: '#fff', strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Two-column: Plan Distribution + Membership Growth */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Plan Distribution */}
              <div className="bg-white dark:bg-[#0c0f14] border border-gray-200 dark:border-white/[0.07] rounded-3xl p-7 shadow-sm hover:shadow-md transition-all">
                <SH title="Plan Distribution" sub="Active members by plan" />
                {loading ? (
                  sk()
                ) : !data?.plan_breakdown?.length ? (
                  <div className="flex flex-col items-center justify-center h-[220px] text-gray-400">
                    <BarChart2 size={32} className="mb-3 opacity-30" />
                    <p className="text-sm font-medium">No plan data yet</p>
                  </div>
                ) : (
                  <div className="h-[220px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data.plan_breakdown}
                          dataKey="count"
                          nameKey="plan"
                          cx="50%"
                          cy="50%"
                          innerRadius={65}
                          outerRadius={90}
                          paddingAngle={4}
                          stroke="none"
                        >
                          {data.plan_breakdown.map((_, i) => (
                            <Cell
                              key={i}
                              fill={COLORS[i % COLORS.length]}
                              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip suffix=" members" />} />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          iconType="circle"
                          wrapperStyle={{ fontSize: 11, fontWeight: 600 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Membership Growth */}
              <div className="bg-white dark:bg-[#0c0f14] border border-gray-200 dark:border-white/[0.07] rounded-3xl p-7 shadow-sm hover:shadow-md transition-all">
                <SH title="Membership Growth" sub="New members per month" />
                {loading ? (
                  sk()
                ) : (
                  <div className="h-[220px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={data?.new_members_trend}
                        margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="memberGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#8b5cf6" stopOpacity={1} />
                            <stop offset="100%" stopColor="#c4b5fd" stopOpacity={0.5} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          strokeOpacity={0.08}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="month"
                          tick={{ fontSize: 11, fill: '#9ca3af', fontWeight: 600 }}
                          axisLine={false}
                          tickLine={false}
                          dy={10}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: '#9ca3af', fontWeight: 600 }}
                          allowDecimals={false}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          cursor={{ fill: 'rgba(139,92,246,0.08)', radius: 8 }}
                          content={<CustomTooltip suffix=" members" />}
                        />
                        <Bar
                          dataKey="members"
                          name="New Members"
                          fill="url(#memberGrad)"
                          radius={[6, 6, 0, 0]}
                          barSize={36}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            {/* Two-column: Top Members + Trainer Hours */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-2">
              {/* Top Members */}
              <div className="bg-white dark:bg-[#0c0f14] border border-gray-200 dark:border-white/[0.07] rounded-3xl p-7 shadow-sm hover:shadow-md transition-all">
                <SH title="Most Active Members" sub="By check-ins in last 30 days" />
                {loading ? (
                  sk(160)
                ) : !data?.top_members?.length ? (
                  <div className="flex flex-col items-center justify-center h-[160px] gap-2 text-gray-400">
                    <Award size={28} className="opacity-30" />{' '}
                    <p className="text-sm font-medium">No check-ins recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {data.top_members.map((m, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
                      >
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm"
                          style={{
                            backgroundColor:
                              i === 0 ? 'rgba(239,175,0,0.15)' : `${COLORS[i % COLORS.length]}15`,
                          }}
                        >
                          {i === 0 ? (
                            <Award size={18} style={{ color: '#efaf00' }} />
                          ) : (
                            <span
                              className="text-xs font-black"
                              style={{ color: COLORS[i % COLORS.length] }}
                            >
                              #{i + 1}
                            </span>
                          )}
                        </div>
                        <p className="font-bold text-sm text-gray-900 dark:text-white flex-1">
                          {m.name}
                        </p>
                        <div className="text-right">
                          <span
                            className="text-sm font-black"
                            style={{ color: COLORS[i % COLORS.length] }}
                          >
                            {m.checkins}
                          </span>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                            Visits
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Trainer Hours */}
              <div className="bg-white dark:bg-[#0c0f14] border border-gray-200 dark:border-white/[0.07] rounded-3xl p-7 shadow-sm hover:shadow-md transition-all">
                <SH title="Trainer Hours" sub="Total hours logged this month" />
                {loading ? (
                  sk(160)
                ) : !data?.trainer_hours?.length ? (
                  <div className="flex flex-col items-center justify-center h-[160px] gap-2 text-gray-400">
                    <Dumbbell size={28} className="opacity-30" />{' '}
                    <p className="text-sm font-medium">No trainer hours logged</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {data.trainer_hours.map((t, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
                      >
                        <div
                          className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm"
                          style={{ backgroundColor: `${COLORS[i % COLORS.length]}15` }}
                        >
                          <Dumbbell size={16} style={{ color: COLORS[i % COLORS.length] }} />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-sm text-gray-900 dark:text-white">
                            {t.trainer_name}
                          </p>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                            {t.days} Days Worked
                          </p>
                        </div>
                        <span
                          className="text-sm font-black"
                          style={{ color: COLORS[i % COLORS.length] }}
                        >
                          {t.hours}h
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ══ REVENUE INSIGHTS TAB ══ */}
        {activeTab === 'revenue' && (
          <PlanGate feature="revenueInsights" featureName="Revenue Insights">
            <div className="space-y-6">
              {/* Revenue KPIs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {revLoading ? (
                  [1, 2, 3, 4].map(i => <div key={i}>{sk(140)}</div>)
                ) : (
                  <>
                    <KPICard
                      icon={TrendingUp}
                      label="This Month Revenue"
                      color="#10b981"
                      value={`${currencySymbol}${(revenueData?.summary?.current_month || data?.monthly_revenue?.at(-1)?.revenue || 0).toLocaleString()}`}
                      sub="total collected"
                      trend={revenueData?.summary?.mom_change || data?.mom_change}
                    />
                    <KPICard
                      icon={DollarSign}
                      label="Last Month"
                      color="#3b82f6"
                      value={`${currencySymbol}${(revenueData?.summary?.last_month || 0).toLocaleString()}`}
                      sub="previous month total"
                    />
                    <KPICard
                      icon={Target}
                      label="Avg per Member"
                      color="#8b5cf6"
                      value={`${currencySymbol}${(revenueData?.summary?.avg_per_member || 0).toLocaleString()}`}
                      sub="revenue per active member"
                    />
                    <KPICard
                      icon={AlertCircle}
                      label="Pending Revenue"
                      color="#f59e0b"
                      value={`${currencySymbol}${(revenueData?.summary?.pending_total || 0).toLocaleString()}`}
                      sub="awaiting collection"
                    />
                  </>
                )}
              </div>

              {/* Monthly Revenue Trend - Full */}
              <div className="bg-white dark:bg-[#0c0f14] border border-gray-200 dark:border-white/[0.07] rounded-3xl p-7 shadow-sm">
                <SH title="Monthly Revenue Trend" sub="Collected vs Pending breakdown" />
                {revLoading ? (
                  sk(280)
                ) : (
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={
                          revenueData?.monthly?.length ? revenueData.monthly : data?.monthly_revenue
                        }
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="revBarGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                            <stop offset="100%" stopColor="#10b981" stopOpacity={0.6} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          strokeOpacity={0.08}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="month"
                          tick={{ fontSize: 11, fill: '#9ca3af', fontWeight: 600 }}
                          axisLine={false}
                          tickLine={false}
                          dy={10}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: '#9ca3af', fontWeight: 600 }}
                          tickFormatter={v =>
                            `${currencySymbol}${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`
                          }
                          axisLine={false}
                          tickLine={false}
                          dx={-8}
                        />
                        <Tooltip content={<CustomTooltip prefix={currencySymbol} />} />
                        <Legend
                          wrapperStyle={{ fontSize: 12, fontWeight: 600, paddingTop: 16 }}
                          iconType="circle"
                        />
                        <Bar
                          dataKey="revenue"
                          name="Collected"
                          fill="url(#revBarGrad)"
                          radius={[6, 6, 0, 0]}
                          barSize={32}
                        />
                        {revenueData?.monthly?.some(m => m.pending) && (
                          <Bar
                            dataKey="pending"
                            name="Pending"
                            fill="#f59e0b"
                            fillOpacity={0.5}
                            radius={[6, 6, 0, 0]}
                            barSize={32}
                          />
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Revenue composition */}
              {!revLoading && revenueData?.summary && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    {
                      label: 'Cash Payments',
                      value: revenueData.summary.cash_total || 0,
                      icon: DollarSign,
                      color: '#10b981',
                    },
                    {
                      label: 'Online / UPI',
                      value: revenueData.summary.online_total || 0,
                      icon: Zap,
                      color: '#3b82f6',
                    },
                    {
                      label: 'Card Payments',
                      value: revenueData.summary.card_total || 0,
                      icon: Shield,
                      color: '#8b5cf6',
                    },
                  ].map(item => (
                    <div
                      key={item.label}
                      className="bg-white dark:bg-[#0c0f14] border border-gray-200 dark:border-white/[0.07] rounded-2xl p-5 shadow-sm flex items-center gap-4"
                    >
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${item.color}15` }}
                      >
                        <item.icon size={20} style={{ color: item.color }} />
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                          {item.label}
                        </p>
                        <p className="text-xl font-black text-gray-900 dark:text-white">
                          {currencySymbol}
                          {item.value.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </PlanGate>
        )}

        {/* ══ RETENTION TAB ══ */}
        {activeTab === 'retention' && (
          <PlanGate feature="advancedAnalytics" featureName="Retention Analytics">
            <div className="space-y-6">
              {/* Retention KPIs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {retLoading ? (
                  [1, 2, 3, 4].map(i => <div key={i}>{sk(140)}</div>)
                ) : (
                  <>
                    <KPICard
                      icon={UserCheck}
                      label="Retention Rate"
                      color="#10b981"
                      value={`${retentionData?.overview?.retention_rate || data?.retention_rate || 0}%`}
                      sub="visited in last 30 days"
                    />
                    <KPICard
                      icon={Flame}
                      label="At-Risk Members"
                      color="#ef4444"
                      value={
                        retentionData?.overview?.at_risk_count || retentionData?.atRisk?.length || 0
                      }
                      sub="inactive 10+ days"
                    />
                    <KPICard
                      icon={UserX}
                      label="Churned (30d)"
                      color="#f59e0b"
                      value={retentionData?.overview?.churned_count || 0}
                      sub="no visit in 30 days"
                    />
                    <KPICard
                      icon={Users}
                      label="Active Members"
                      color="#3b82f6"
                      value={retentionData?.overview?.active_count || data?.total_members || 0}
                      sub="visited in last 30 days"
                    />
                  </>
                )}
              </div>

              {/* Retention Rate Gauge */}
              {!retLoading && retentionData?.overview && (
                <div className="bg-white dark:bg-[#0c0f14] border border-gray-200 dark:border-white/[0.07] rounded-3xl p-7 shadow-sm">
                  <SH title="Member Health Overview" sub="Breakdown of engagement levels" />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      {
                        label: 'Highly Active',
                        desc: 'Visited 4+ times/month',
                        count: retentionData.overview.highly_active || 0,
                        color: '#10b981',
                        pct: (
                          ((retentionData.overview.highly_active || 0) /
                            Math.max(retentionData.overview.total || 1, 1)) *
                          100
                        ).toFixed(0),
                      },
                      {
                        label: 'Moderately Active',
                        desc: '1-3 visits/month',
                        count: retentionData.overview.moderate || 0,
                        color: '#3b82f6',
                        pct: (
                          ((retentionData.overview.moderate || 0) /
                            Math.max(retentionData.overview.total || 1, 1)) *
                          100
                        ).toFixed(0),
                      },
                      {
                        label: 'Inactive / At Risk',
                        desc: 'No visit in 10+ days',
                        count: retentionData.overview.at_risk_count || 0,
                        color: '#ef4444',
                        pct: (
                          ((retentionData.overview.at_risk_count || 0) /
                            Math.max(retentionData.overview.total || 1, 1)) *
                          100
                        ).toFixed(0),
                      },
                    ].map(item => (
                      <div
                        key={item.label}
                        className="border border-gray-100 dark:border-white/5 rounded-2xl p-5"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-black text-sm text-gray-900 dark:text-white">
                              {item.label}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
                          </div>
                          <span className="text-2xl font-black" style={{ color: item.color }}>
                            {item.count}
                          </span>
                        </div>
                        <div className="h-2 bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${item.pct}%`, backgroundColor: item.color }}
                          />
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1.5 text-right font-bold">
                          {item.pct}%
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* At-Risk Member List */}
              <div className="bg-white dark:bg-[#0c0f14] border border-gray-200 dark:border-white/[0.07] rounded-3xl p-7 shadow-sm">
                <SH
                  title="At-Risk Members"
                  sub="Members who haven't visited recently — reach out before they churn"
                  action={
                    <span className="text-xs font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 px-2.5 py-1 rounded-full border border-amber-200 dark:border-amber-500/20">
                      {retentionData?.atRisk?.length || 0} members
                    </span>
                  }
                />
                {retLoading ? (
                  sk(200)
                ) : !retentionData?.atRisk?.length ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <Shield size={32} className="mb-3 opacity-30 text-emerald-500" />
                    <p className="font-bold text-sm text-emerald-600 dark:text-emerald-400">
                      All members are engaged!
                    </p>
                    <p className="text-xs mt-1">No at-risk members detected right now.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-white/5">
                    {retentionData.atRisk.slice(0, 10).map((m, i) => (
                      <AtRiskRow key={i} member={m} currencySymbol={currencySymbol} />
                    ))}
                    {retentionData.atRisk.length > 10 && (
                      <p className="text-center text-sm text-gray-400 py-4">
                        +{retentionData.atRisk.length - 10} more at-risk members
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </PlanGate>
        )}
      </div>
    </AdminLayout>
  );
}
